import { useState } from 'react';
import { pb } from '@/lib/pb';
import { Activity } from 'lucide-react';

export function Login({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      if (isRegistering) {
        await pb.collection('users').create({
          email,
          password,
          passwordConfirm: password,
        }, { requestKey: null });
      }
      await pb.collection('users').authWithPassword(email, password, { requestKey: null });
      
      // Keep user_profiles in sync with Garmin credentials for automatic polling
      try {
        await pb.collection('user_profiles').update(pb.authStore.model!.id, {
          garmin_email: email,
          garmin_password: password
        }, { requestKey: null });
      } catch (e: any) {
        if (e?.status === 404) {
          // Profile doesn't exist yet, create it
          await pb.collection('user_profiles').create({
            id: pb.authStore.model!.id,
            athlete_name: email.split('@')[0],
            garmin_email: email,
            garmin_password: password
          }, { requestKey: null });
        } else {
          console.error('Failed to update user_profiles', e);
        }
      }
      
      onLogin();
    } catch (err: any) {
      setError(err.message || 'Failed to authenticate');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <div className="w-full max-w-md p-5 sm:p-8 mx-3 sm:mx-0 bg-card rounded-2xl border border-border shadow-lg">
        <div className="flex flex-col items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-foreground flex items-center justify-center shadow-md">
            <Activity className="w-6 h-6 text-background" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Athletics Dashboard</h1>
          <p className="text-sm text-muted-foreground">Sign in to your private dashboard</p>
        </div>

        {error && <div className="mb-4 p-3 bg-destructive/10 text-destructive text-sm rounded-lg">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              required
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Authenticating...' : (isRegistering ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <div className="mt-6 text-center text-sm">
          <button
            type="button"
            onClick={() => setIsRegistering(!isRegistering)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {isRegistering ? 'Already have an account? Sign in' : 'Need an account? Register'}
          </button>
        </div>
      </div>
    </div>
  );
}
